import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, Image as ImageIcon, AlertCircle, Clipboard } from 'lucide-react';
import { uploadTaskImage, uploadCommentImage, validateImageFile, ImageUploadResult } from '@/services/productManagerImageService';
import { useClipboardImagePaste } from '@/hooks/useClipboardImagePaste';

interface ImageUploadProps {
  onImagesUploaded: (images: ImageUploadResult[]) => void;
  onError?: (error: string) => void;
  entityType: 'task' | 'comment';
  entityId: string;
  maxImages?: number;
  disabled?: boolean;
  className?: string;
  enableClipboardPaste?: boolean;
}

interface UploadingImage {
  file: File;
  progress: number;
  error?: string;
  result?: ImageUploadResult;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onImagesUploaded,
  onError,
  entityType,
  entityId,
  maxImages = 5,
  disabled = false,
  className = '',
  enableClipboardPaste = true
}) => {
  const [uploadingImages, setUploadingImages] = useState<UploadingImage[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [pasteActive, setPasteActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pasteZoneRef = useRef<HTMLDivElement>(null);

  // Initialize clipboard paste functionality
  const { attachPasteListener, detachPasteListener, isPasteReady } = useClipboardImagePaste({
    onImagesPasted: (files: File[]) => {
      setPasteActive(true);
      const fileList = new DataTransfer();
      files.forEach(file => fileList.items.add(file));
      handleFiles(fileList.files);
      // Reset paste active state after a brief moment
      setTimeout(() => setPasteActive(false), 1000);
    },
    onError,
    maxImages,
    disabled: disabled || !enableClipboardPaste,
    enablePaste: enableClipboardPaste
  });

  const handleFiles = useCallback(async (files: FileList) => {
    const fileArray = Array.from(files);
    
    // Validate number of files
    if (fileArray.length > maxImages) {
      onError?.(`Maximum ${maxImages} images allowed`);
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    for (const file of fileArray) {
      const validation = validateImageFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        onError?.(validation.error || 'Invalid file');
      }
    }

    if (validFiles.length === 0) return;

    // Initialize uploading state
    const initialUploading: UploadingImage[] = validFiles.map(file => ({
      file,
      progress: 0
    }));

    setUploadingImages(initialUploading);

    // Upload files
    const uploadPromises = validFiles.map(async (file, index) => {
      try {
        const uploadFunction = entityType === 'task' ? uploadTaskImage : uploadCommentImage;
        
        const result = await uploadFunction(
          entityId,
          file,
          (progress) => {
            setUploadingImages(prev => 
              prev.map((item, i) => 
                i === index ? { ...item, progress } : item
              )
            );
          }
        );

        // Update with result
        setUploadingImages(prev => 
          prev.map((item, i) => 
            i === index ? { ...item, progress: 100, result } : item
          )
        );

        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        
        setUploadingImages(prev => 
          prev.map((item, i) => 
            i === index ? { ...item, error: errorMessage } : item
          )
        );
        
        onError?.(errorMessage);
        return null;
      }
    });

    try {
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter((result): result is ImageUploadResult => result !== null);
      
      if (successfulUploads.length > 0) {
        onImagesUploaded(successfulUploads);
      }
      
      // Clear uploading state immediately after successful uploads
      setTimeout(() => {
        setUploadingImages([]);
      }, 500);
    } catch (error) {
      console.error('Upload error:', error);
    }
  }, [entityId, entityType, maxImages, onError, onImagesUploaded]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [disabled, handleFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  }, [handleFiles]);

  const openFileDialog = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  // Attach/detach paste listener
  useEffect(() => {
    if (enableClipboardPaste && pasteZoneRef.current) {
      attachPasteListener(pasteZoneRef.current);
      return () => {
        if (pasteZoneRef.current) {
          detachPasteListener(pasteZoneRef.current);
        }
      };
    }
  }, [enableClipboardPaste, attachPasteListener, detachPasteListener]);

  const isUploading = uploadingImages.length > 0;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <Card
        ref={pasteZoneRef}
        className={`border-2 border-dashed transition-all duration-200 cursor-pointer ${
          dragActive
            ? 'border-violet-500 bg-violet-50 dark:bg-violet-950'
            : pasteActive
            ? 'border-green-500 bg-green-50 dark:bg-green-950'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${
          enableClipboardPaste && isPasteReady ? 'focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-opacity-50' : ''
        }`}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
        tabIndex={enableClipboardPaste && isPasteReady ? 0 : -1}
      >
        <CardContent className="flex flex-col items-center justify-center py-8 px-4">
          <div className="flex items-center justify-center mb-4">
            {pasteActive ? (
              <Clipboard className="h-12 w-12 text-green-500 animate-pulse" />
            ) : (
              <ImageIcon className="h-12 w-12 text-gray-400" />
            )}
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              {pasteActive
                ? 'Images pasted successfully!'
                : dragActive
                ? 'Drop images here'
                : 'Click to upload or drag and drop'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              JPG, PNG, GIF, WebP up to 5MB (max {maxImages} images)
            </p>
            {enableClipboardPaste && isPasteReady && !pasteActive && (
              <p className="text-xs text-violet-600 dark:text-violet-400 font-medium">
                💡 Tip: Press Ctrl+V to paste images from clipboard
              </p>
            )}
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                openFileDialog();
              }}
            >
              <Upload className="h-4 w-4 mr-2" />
              Choose Files
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Upload Progress */}
      {isUploading && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
            Uploading Images...
          </h4>
          {uploadingImages.map((item, index) => (
            <Card key={index} className="p-3">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {item.error ? (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  ) : item.result ? (
                    <ImageIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <ImageIcon className="h-5 w-5 text-blue-500" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {item.file.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {(item.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  
                  {item.error ? (
                    <p className="text-xs text-red-500 mt-1">{item.error}</p>
                  ) : item.result ? (
                    <p className="text-xs text-green-500 mt-1">Upload complete</p>
                  ) : (
                    <div className="mt-2">
                      <Progress value={item.progress} className="h-1" />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {item.progress}%
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;